import React from "react";
import type { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { RequiredLabel } from "@/components/ui/required-label";
import { useI18n } from "@/context/i18n";

import type { TProductFormData } from "../../products.schema";
import {
  calculateNetWithGatewayCosts,
  formatProductPriceBrl,
} from "../../utils/soulhub-gateway-pricing.util";

export type SoulhubGatewayDisplayMode = "full" | "commissioned";

export interface SoulhubGatewayPricingSectionProps {
  form: UseFormReturn<TProductFormData>;
  cfgPrefix: string;
  /** full: NET + custos com %; commissioned: só % e taxa fixa (sem linha de custo total). */
  displayMode?: SoulhubGatewayDisplayMode;
}

export const SoulhubGatewayPricingSection: React.FC<
  SoulhubGatewayPricingSectionProps
> = ({ form, cfgPrefix, displayMode = "full" }) => {
  const { t } = useI18n();

  const netPrice = form.watch(`${cfgPrefix}.net_price` as any);
  const gatewayPercent = form.watch(`${cfgPrefix}.gateway_fee_percent` as any);
  const gatewayPerSale = form.watch(`${cfgPrefix}.gateway_fee_per_sale` as any);

  const netWithGateway = React.useMemo(
    () =>
      calculateNetWithGatewayCosts(
        Number(netPrice ?? 0),
        Number(gatewayPercent ?? 0),
        Number(gatewayPerSale ?? 0),
      ),
    [netPrice, gatewayPercent, gatewayPerSale],
  );

  return (
    <div className="space-y-4 rounded-md border border-dashed p-4 bg-muted/20">
      <p className="text-sm font-medium">{t("products.gateway.sectionTitle")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name={`${cfgPrefix}.gateway_fee_percent` as any}
          render={({ field }) => (
            <FormItem>
              <RequiredLabel required>
                {t("products.gateway.fields.fee_percent")}
              </RequiredLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={
                    field.value === undefined || field.value === null
                      ? ""
                      : String(field.value)
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(v === "" ? undefined : Number(v));
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`${cfgPrefix}.gateway_fee_per_sale` as any}
          render={({ field }) => (
            <FormItem>
              <RequiredLabel required>
                {t("products.gateway.fields.fee_per_sale")}
              </RequiredLabel>
              <FormControl>
                <RealCurrencyInput
                  value={Number(field.value ?? 0)}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {displayMode === "full" ? (
          <FormItem className="md:col-span-2">
            <FormLabel>{t("products.gateway.fields.net_with_gateway_costs")}</FormLabel>
            <FormControl>
              <Input
                readOnly
                className="bg-muted"
                value={formatProductPriceBrl(netWithGateway)}
              />
            </FormControl>
            <p className="text-xs text-muted-foreground">
              {t("products.gateway.fields.net_with_gateway_costs_hint")}
            </p>
          </FormItem>
        ) : null}
      </div>
    </div>
  );
};
